// @ts-nocheck

import notification from 'antd/lib/notification';
import type { SelectProps } from 'antd/lib/select';
import debounce from 'lodash/debounce';
import isNil from 'lodash/isNil';
import uniqBy from 'lodash/uniqBy';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';

import { Option as SelectOption, Select } from '../ant/Select';

import { Spinner } from './../../components';
import { isNumberValue } from './../../lib/helpers';

type FetchParams = {
  search?: string;
  page?: number;
  pageSize?: number;
};

type FetchResponse<Item> = {
  data: Item[];
};

export type AutocompleteSelectProps<Item = Record<string, unknown>> = Omit<
  SelectProps,
  'onFocus'
> & {
  /**
   * Функция, для подгрузки данных списка.
   *
   * По-умолчанию, когда список только появился на странице,
   * подгружается 50 записей.
   */
  fetch: (params: FetchParams) => Promise<FetchResponse<Item>>;
  /**
   * Функция для подгрузки одного значения.
   *
   * Используется для добавления с список значения,
   * id которого пришел в value и которое отсутствует
   * с списке подгруженных по-умолчанию.
   */
  fetchSingle: (
    id: number,
    params?: { returnDeleted?: boolean }
  ) => Promise<Item | null | undefined>;
  /**
   * Имя ключа, для которого выбирается значение
   * @default id
   */
  valueKey?: string;
  /**
   * Текст сообщения при отсутствии данных
   * @default Нет данных
   */
  notFoundText?: string;
  /**
   * Функция для отображения компонента списка (Option)
   * @param item Элемент списка
   * @param Option Компонент списка
   */
  renderOption: (item: Item, Option: typeof SelectOption) => ReactNode;
  /**
   * Подгружать ли данные при фокусе на селекте
   * @default false
   */
  fetchOnFocus?: boolean;
  /**
   * Пропсы, при изменении которых необходима переподгрузка данных
   */
  refetchParams?: unknown;
  /**
   * Функция вызываемая при изменении значения
   * @param value Значение по ключу valueKey
   * @param optionPayload Данные из data
   */
  onChange?: SelectProps['onChange'];
  onFocus?: SelectProps['onFocus'];
};

/**
 * Ассинхронный селект с возможностью поиска.
 */
const AutocompleteSelect = <Item,>(props: AutocompleteSelectProps<Item>) => {
  const mounted = useRef(true); //контроль того, что компонент не удален во время асинхронной операции
  const requestSequence = useRef(0);
  const [data, setData] = useState<Item[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetch = useCallback(
    async (search: string) => {
      if (!mounted.current) return;
      const requestId = ++requestSequence.current;
      setIsLoading(true);
      try {
        // отправляем запрос с ограничением в 50 результатов
        const { data } = await props.fetch({
          search,
          page: 1,
          pageSize: 50,
        });
        if (mounted.current && requestId === requestSequence.current) {
          setData(data);
        }
      } catch (error) {
        if (mounted.current && requestId === requestSequence.current) {
          console.error(error);
          notification.error({
            message: 'Ошибка',
            description: error instanceof Error ? error.message : String(error),
          });
        }
      } finally {
        if (mounted.current && requestId === requestSequence.current) {
          setIsLoading(false);
        }
      }
    },
    [props.fetch]
  );

  /**
   * Функция для подгрузки данных
   *
   * Если value не указан, то подгружаются первые 50 записей
   * иначе подгружается 50 + 1 запись, связанная с value
   *
   * @param value Текущее значение селекта, которое нужно подгрузить
   */
  const fetchData = useCallback(
    async (value?: number) => {
      const { disabled, fetchSingle, fetch } = props;
      if (disabled || !mounted.current) return;
      const requestId = ++requestSequence.current;
      setIsLoading(true);
      try {
        const { data } = await fetch({
          page: 1,
          pageSize: 50,
        });
        if (!mounted.current || requestId !== requestSequence.current) return;
        // Список показываем сразу, чтобы ошибка загрузки value его не скрыла
        setData(data);
        // Проп fetchSingle обязателен
        if (typeof fetchSingle !== 'function')
          throw new Error('Prop fetchSingle must be a function');
        // Если value - число
        if (typeof value === 'number') {
          // Запрашиваем запись
          const loadedValue = await fetchSingle(value, {
            returnDeleted: true,
          });
          if (!mounted.current || requestId !== requestSequence.current) return;
          // И добавляем эту запись к загруженному списку, если она существует
          if (!isNil(loadedValue)) {
            setData(uniqBy([loadedValue, ...data], 'id'));
          }
        }
      } catch (error) {
        if (mounted.current && requestId === requestSequence.current) {
          console.error(error);
          notification.error({
            message: 'Ошибка',
            description: error instanceof Error ? error.message : String(error),
          });
        }
      } finally {
        if (mounted.current && requestId === requestSequence.current) {
          setIsLoading(false);
        }
      }
    },
    [props]
  );

  const handleFocus = async (
    e: Parameters<NonNullable<SelectProps['onFocus']>>[0]
  ) => {
    const { onFocus, fetchOnFocus } = props;
    if (onFocus) {
      onFocus(e);
    }
    if (fetchOnFocus) {
      await fetchData();
    }
  };

  const handleChange = async (
    value: Parameters<NonNullable<SelectProps['onChange']>>[0],
    option: Parameters<NonNullable<SelectProps['onChange']>>[1]
  ) => {
    const { onChange } = props;
    if (value === undefined) await fetchData();
    if (onChange) {
      onChange(value, option);
    }
  };

  const handleSearch = useMemo(() => debounce(fetch, 500), [fetch]);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      requestSequence.current += 1;
    };
  }, []);

  useEffect(() => {
    return () => {
      handleSearch.cancel();
    };
  }, [handleSearch]);

  useEffect(() => {
    if (!props.disabled) {
      fetchData(props.value);
    }
    // вернуть props.value если что-то не так
    // eslint-disable-next-line
  }, [props.refetchParams]);

  const {
    notFoundText = 'Не найдено',
    renderOption,
    value,
    refetchParams,
    fetchSingle,
    fetchOnFocus,
    valueKey,
    fetch: ss,
    ...selectProps
  } = props;

  const val = value
    ? isNumberValue(String(value))
      ? parseFloat(String(value))
      : value
    : value;
  return (
    <Select
      showSearch
      onSearch={handleSearch}
      filterOption={false}
      defaultActiveFirstOption={false}
      notFoundContent={<Spinner isLoading={isLoading}>{notFoundText}</Spinner>}
      defaultValue={null}
      loading={isLoading}
      allowClear
      {...selectProps}
      value={selectProps.mode === 'multiple' && val === undefined ? [] : val}
      onFocus={handleFocus}
      onChange={handleChange}
    >
      {isLoading
        ? null
        : data?.map((item) => renderOption(item, SelectOption)) ?? null}
    </Select>
  );
};

export default AutocompleteSelect;
